import { useRef, useState } from 'react';
import { Page } from 'react-pdf/dist/cjs';
import { OnPageLoadSuccess, OnRenderSuccess } from 'react-pdf/dist/cjs/shared/types';
import { v4 as uuidv4 } from 'uuid';
import { ExtractionSpecType } from '../../types';
import { percentageToPixels, pixelsToPercentage } from '../../utils/conversions';
import './styles.css';

type PDFPagePropsType = {
    page: number;
    handleExtraction: (extractionSpec: ExtractionSpecType) => void;
    onLoadSuccess?: OnPageLoadSuccess;
    onRenderSuccess?: OnRenderSuccess;
    disabled: boolean;
    pendingPageExtractionSpecs: ExtractionSpecType[];
};
function PDFPage({
    page,
    handleExtraction,
    onLoadSuccess,
    pendingPageExtractionSpecs,
    disabled,
    onRenderSuccess,
}: PDFPagePropsType) {
    const [startingPosition, setStartingPosition] = useState<{ x: number; y: number } | null>(null);
    const [selectionBox, setSelectionBox] = useState<{ x: number; y: number; width: number; height: number } | null>(
        null
    );
    const [isActive, setIsActive] = useState(false);
    const [hasPageBeenRendered, setHasPageBeenRendered] = useState(false);

    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const handleOnMouseDown = (e: React.MouseEvent) => {
        if (disabled) {
            return;
        }
        if (!canvasRef.current) {
            setStartingPosition(null);
            setSelectionBox(null);
            return;
        }
        const canvasBoundingBox = canvasRef.current.getBoundingClientRect();
        const x = Math.max(e.clientX - canvasBoundingBox.left, 0);
        const y = Math.max(e.clientY - canvasBoundingBox.top, 0);

        setStartingPosition({ x, y });
        setIsActive(true);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!startingPosition || !canvasRef.current || !isActive) {
            return;
        }

        const canvasBoundingBox = canvasRef.current.getBoundingClientRect();
        const x = Math.max(e.clientX - canvasBoundingBox.left, 0);
        const y = Math.max(e.clientY - canvasBoundingBox.top, 0);

        const top = Math.min(startingPosition.y, y);
        const left = Math.min(startingPosition.x, x);
        const width = Math.abs(x - startingPosition.x);
        const height = Math.abs(y - startingPosition.y);
        setSelectionBox({
            x: left,
            y: top,
            width,
            height,
        });
    };

    const handleOnMouseUp = (e: React.MouseEvent) => {
        if (!isActive) {
            return;
        }
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');

        if (selectionBox && selectionBox.width >= 50 && selectionBox.height >= 50 && canvas && context) {
            const canvasBoundingBox = canvas.getBoundingClientRect();
            const x = pixelsToPercentage(selectionBox.x, canvasBoundingBox.width);
            const y = pixelsToPercentage(selectionBox.y, canvasBoundingBox.height);
            const width = pixelsToPercentage(selectionBox.width, canvasBoundingBox.width);
            const height = pixelsToPercentage(selectionBox.height, canvasBoundingBox.height);
            const xPX = percentageToPixels(x, canvas.width);
            const yPX = percentageToPixels(y, canvas.height);
            const widthPX = percentageToPixels(width, canvas.width);
            const heightPX = percentageToPixels(height, canvas.height);
            const extractionSpec: ExtractionSpecType = {
                page,
                id: uuidv4(),
                xPercentage: x,
                yPercentage: y,
                widthPercentage: width,
                heightPercentage: height,
                widthPX,
                heightPX,
                imageData: context.getImageData(xPX, yPX, widthPX, heightPX),
            };
            handleExtraction(extractionSpec);
        }
        setStartingPosition(null);
        setSelectionBox(null);
        setIsActive(false);
    };
    return (
        <>
            <div className="relative">
                <Page
                    renderTextLayer={true}
                    renderAnnotationLayer={false}
                    canvasRef={canvasRef}
                    onMouseDown={handleOnMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleOnMouseUp}
                    pageNumber={page}
                    scale={1.5}
                    onLoadSuccess={onLoadSuccess}
                    onRenderSuccess={(page) => {
                        setHasPageBeenRendered(true);
                        if (onRenderSuccess) {
                            onRenderSuccess(page);
                        }
                    }}
                />
                {selectionBox && (
                    <SelectionBox
                        top={selectionBox?.y || 0}
                        left={selectionBox?.x || 0}
                        width={selectionBox?.width || 0}
                        height={selectionBox?.height || 0}
                    />
                )}
                {hasPageBeenRendered &&
                    pendingPageExtractionSpecs.map((pendingExtraction) => (
                        <PendingPageExtraction
                            key={pendingExtraction.id}
                            canvasBoundingBox={canvasRef.current?.getBoundingClientRect()}
                            extractionSpec={pendingExtraction}
                        />
                    ))}
            </div>
        </>
    );
}

type PendingPageExtractionPropsType = {
    canvasBoundingBox: DOMRect | undefined;
    extractionSpec: ExtractionSpecType;
};
function PendingPageExtraction({ canvasBoundingBox, extractionSpec }: PendingPageExtractionPropsType) {
    if (!canvasBoundingBox) {
        return null;
    }
    const top = percentageToPixels(extractionSpec.yPercentage, canvasBoundingBox.height);
    const left = percentageToPixels(extractionSpec.xPercentage, canvasBoundingBox.width);
    const width = percentageToPixels(extractionSpec.widthPercentage, canvasBoundingBox.width);
    const height = percentageToPixels(extractionSpec.heightPercentage, canvasBoundingBox.height);
    return <SelectionBox top={top} left={left} width={width} height={height} />;
}

type SelectionBoxPropsType = {
    top: number;
    left: number;
    width: number;
    height: number;
};

function SelectionBox({ top, left, width, height }: SelectionBoxPropsType) {
    return (
        <div
            className="selection-box"
            style={{
                top,
                left,
                width,
                height,
            }}
        ></div>
    );
}

export default PDFPage;
